import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowRight,
  User,
  Phone,
  Mail,
  Building,
  CreditCard,
  DollarSign,
  FileText,
  Calendar,
  Clock,
  TrendingUp,
  CheckCircle,
  XCircle,
  AlertCircle,
  Edit,
  Plus,
  Download
} from 'lucide-react';
import AdminLayout from '../../../components/admin/AdminLayout';
import { useAdminAuth } from '../../../contexts/AdminAuthContext';

const CreditorDetailsPage = () => {
  const navigate = useNavigate();
  const { creditorId } = useParams();
  const { hasPermission } = useAdminAuth();
  const [creditor, setCreditor] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  // Mock data - سيتم استبداله بـ API
  useEffect(() => {
    setTimeout(() => {
      // Mock creditor data
      const mockCreditor = {
        id: creditorId,
        name: 'شركة المحاماة المتقدمة',
        type: 'company',
        phone: '0501234567',
        email: 'info@advanced-law.com',
        bankAccount: 'SA1234567890123456789012',
        category: 'خدمات قانونية',
        status: 'active',
        totalDue: 45000,
        totalPaid: 120000,
        remaining: 45000,
        avgPaymentDays: 28,
        joinDate: '2023-05-15',
        lastTransaction: '2024-11-25'
      };

      // Mock invoices
      const mockInvoices = [
        {
          id: 'INV-2024-101',
          date: '2024-11-25',
          dueDate: '2024-12-10',
          amount: 15000,
          paid: 0,
          remaining: 15000,
          status: 'pending'
        },
        {
          id: 'INV-2024-089',
          date: '2024-11-10',
          dueDate: '2024-11-25',
          amount: 20000,
          paid: 0,
          remaining: 20000,
          status: 'overdue'
        },
        {
          id: 'INV-2024-067',
          date: '2024-10-20',
          dueDate: '2024-11-05',
          amount: 10000,
          paid: 0,
          remaining: 10000,
          status: 'overdue'
        },
        {
          id: 'INV-2024-045',
          date: '2024-09-15',
          dueDate: '2024-10-01',
          amount: 25000,
          paid: 25000,
          remaining: 0,
          status: 'paid'
        },
        {
          id: 'INV-2024-023',
          date: '2024-08-10',
          dueDate: '2024-08-25',
          amount: 30000,
          paid: 30000,
          remaining: 0,
          status: 'paid'
        }
      ];

      setCreditor(mockCreditor);
      setInvoices(mockInvoices);
      setLoading(false);
    }, 500);
  }, [creditorId]);

  // Get status badge for invoices
  const getInvoiceStatusBadge = (status) => {
    const statusConfig = {
      paid: {
        label: 'مدفوعة',
        icon: CheckCircle,
        className: 'bg-green-50 text-green-700 border-green-200'
      },
      pending: {
        label: 'معلقة',
        icon: Clock,
        className: 'bg-yellow-50 text-yellow-700 border-yellow-200'
      },
      overdue: {
        label: 'متأخرة',
        icon: AlertCircle,
        className: 'bg-red-50 text-red-700 border-red-200'
      }
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold border ${config.className}`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-[#1E3A8A] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">جاري تحميل بيانات الدائن...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (!creditor) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <p className="text-gray-600">لم يتم العثور على الدائن</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/admin/finance/creditors')}
            className="inline-flex items-center gap-2 text-gray-600 hover:text-[#1E3A8A] mb-4 transition-colors"
          >
            <ArrowRight className="w-5 h-5" />
            <span className="font-semibold">العودة للدائنين</span>
          </button>

          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-black text-[#1E3A8A] mb-2">تفاصيل الدائن</h1>
              <p className="text-gray-600">{creditor.name}</p>
            </div>

            {hasPermission('finance.creditors.edit') && (
              <button
                onClick={() => navigate(`/admin/finance/creditors/${creditorId}/edit`)}
                className="inline-flex items-center gap-2 px-4 py-2 border-2 border-[#1E3A8A] text-[#1E3A8A] rounded-lg font-bold hover:bg-blue-50 transition-all"
              >
                <Edit className="w-5 h-5" />
                <span>تعديل البيانات</span>
              </button>
            )}
          </div>
        </div>

        {/* Creditor Info Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Basic Info Card */}
          <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 p-6">
            <h2 className="text-lg font-black text-gray-800 mb-4 flex items-center gap-2">
              <User className="w-5 h-5 text-[#1E3A8A]" />
              البيانات التعريفية
            </h2>
            
            <div className="space-y-3">
              <div>
                <p className="text-xs text-gray-500 mb-1">الاسم</p>
                <p className="font-bold text-gray-800">{creditor.name}</p>
              </div>

              <div>
                <p className="text-xs text-gray-500 mb-1">النوع</p>
                <p className="font-semibold text-gray-700">
                  {creditor.type === 'individual' ? 'فرد' : 'شركة'}
                </p>
              </div>

              <div>
                <p className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                  <Phone className="w-3 h-3" />
                  رقم الهاتف
                </p>
                <p className="font-semibold text-gray-700" dir="ltr">{creditor.phone}</p>
              </div>

              <div>
                <p className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                  <Mail className="w-3 h-3" />
                  البريد الإلكتروني
                </p>
                <p className="font-semibold text-gray-700 text-sm break-all">{creditor.email}</p>
              </div>

              {creditor.bankAccount && (
                <div>
                  <p className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                    <CreditCard className="w-3 h-3" />
                    الحساب البنكي
                  </p>
                  <p className="font-mono text-sm text-gray-700">{creditor.bankAccount}</p>
                </div>
              )}

              <div>
                <p className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                  <Building className="w-3 h-3" />
                  التصنيف
                </p>
                <p className="font-semibold text-gray-700">{creditor.category}</p>
              </div>
            </div>
          </div>

          {/* Financial Panel */}
          <div className="lg:col-span-2 bg-white rounded-lg shadow-sm border-2 border-gray-200 p-6">
            <h2 className="text-lg font-black text-gray-800 mb-4 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-[#1E3A8A]" />
              اللوحة المالية
            </h2>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-blue-50 rounded-lg p-4 border-2 border-blue-200">
                <p className="text-xs text-gray-600 mb-1">إجمالي المستحقات</p>
                <p className="text-2xl font-black text-blue-600">
                  {creditor.totalDue.toLocaleString()} ر.س
                </p>
              </div>

              <div className="bg-green-50 rounded-lg p-4 border-2 border-green-200">
                <p className="text-xs text-gray-600 mb-1">المدفوع</p>
                <p className="text-2xl font-black text-green-600">
                  {creditor.totalPaid.toLocaleString()} ر.س
                </p>
              </div>

              <div className="bg-red-50 rounded-lg p-4 border-2 border-red-200">
                <p className="text-xs text-gray-600 mb-1">المتبقي</p>
                <p className="text-2xl font-black text-red-600">
                  {creditor.remaining.toLocaleString()} ر.س
                </p>
              </div>

              <div className="bg-yellow-50 rounded-lg p-4 border-2 border-yellow-200">
                <p className="text-xs text-gray-600 mb-1">متوسط مدة السداد</p>
                <p className="text-2xl font-black text-yellow-600">
                  {creditor.avgPaymentDays} يوم
                </p>
              </div>
            </div>

            {/* Timeline Info */}
            <div className="mt-6 grid grid-cols-2 gap-4">
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-xs text-gray-500">تاريخ الانضمام</p>
                  <p className="font-semibold text-gray-700">{creditor.joinDate}</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-xs text-gray-500">آخر تعامل</p>
                  <p className="font-semibold text-gray-700">{creditor.lastTransaction}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Invoices Section */}
        <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-black text-gray-800 flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#1E3A8A]" />
              الفواتير المرتبطة ({invoices.length})
            </h2>

            <div className="flex items-center gap-2">
              {hasPermission('finance.payment') && (
                <button
                  onClick={() => alert('تسجيل دفعة جديدة')}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg font-bold hover:bg-green-700 transition-all"
                >
                  <Plus className="w-4 h-4" />
                  <span>تسجيل دفعة</span>
                </button>
              )}

              {hasPermission('finance.export') && (
                <button
                  onClick={() => alert('تصدير كشف الحساب')}
                  className="inline-flex items-center gap-2 px-4 py-2 border-2 border-[#1E3A8A] text-[#1E3A8A] rounded-lg font-bold hover:bg-blue-50 transition-all"
                >
                  <Download className="w-4 h-4" />
                  <span>تصدير</span>
                </button>
              )}
            </div>
          </div>

          {/* Invoices Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b-2 border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-right text-sm font-bold text-gray-700">رقم الفاتورة</th>
                  <th className="px-4 py-3 text-right text-sm font-bold text-gray-700">التاريخ</th>
                  <th className="px-4 py-3 text-right text-sm font-bold text-gray-700">تاريخ الاستحقاق</th>
                  <th className="px-4 py-3 text-right text-sm font-bold text-gray-700">المبلغ الكلي</th>
                  <th className="px-4 py-3 text-right text-sm font-bold text-gray-700">المدفوع</th>
                  <th className="px-4 py-3 text-right text-sm font-bold text-gray-700">المتبقي</th>
                  <th className="px-4 py-3 text-center text-sm font-bold text-gray-700">الحالة</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {invoices.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                      <FileText className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                      <p>لا توجد فواتير</p>
                    </td>
                  </tr>
                ) : (
                  invoices.map((invoice) => (
                    <tr key={invoice.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3">
                        <span className="font-bold text-[#1E3A8A]">{invoice.id}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-700">{invoice.date}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-700">{invoice.dueDate}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-bold text-gray-800">{invoice.amount.toLocaleString()} ر.س</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-semibold text-green-600">{invoice.paid.toLocaleString()} ر.س</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`font-bold ${invoice.remaining > 0 ? 'text-red-600' : 'text-green-600'}`}>
                          {invoice.remaining.toLocaleString()} ر.س
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {getInvoiceStatusBadge(invoice.status)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default CreditorDetailsPage;
